/**
 * <TutorManageSessionDialog> — les boîtes de « Gérer une séance » (template).
 *
 * Les huit `DialogM` de l'ancien parcours (`ManageRDVScreen`, `SelectSituationComponent`,
 * `MoveSessionReasonComponent`), mot pour mot, une seule forme (`<ConfirmDialog>`) et une à la fois :
 * la présence qui compte (à l'ouverture d'une absence, et au refus), l'impact qui a changé depuis
 * l'aperçu, le support (« Autre », ou un créneau que « Libérer des créneaux » ne prend pas), le
 * dernier mot avant un retard, l'accord de l'élève, et le créneau déjà replanifié qu'on écraserait.
 *
 * Coquille seule, comme `<TutorSessionNoticeDialog>` : les mots sont ici, la boîte ouverte et les
 * gestes arrivent en props.
 */
import React from "react"

import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { tutorChangeNotice } from "../../molecules/TutorAbsenceImpact"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorAbsenceImpactStudent } from "../../../ports/primary/ui/types/tutorLives"

/** Les boîtes du parcours — une à la fois. */
export type TutorManageSessionDialogKind =
    /** À l'ouverture d'une absence, un élève en premier mois (`hypercare_block`) : la présence compte. */
    | { kind: "hypercare"; message: string }
    /** La déclaration est refusée : l'impact a changé depuis l'aperçu. */
    | { kind: "impactChanged" }
    /** Le même refus, pour un élève en premier mois. */
    | { kind: "hypercareRefused"; message: string }
    /** « Autre », ou « plus disponible » sur un live que « Libérer des créneaux » ne prend pas. */
    | { kind: "support"; reason: "other" | "unavailable" }
    /** « Confirmer » le retard : ce que les élèves recevront. */
    | { kind: "confirmDelay" }
    /** Cocher l'accord de l'élève : le lui a-t-on vraiment demandé ? */
    | { kind: "confirmStudentValidation" }
    /** Déplacer le créneau d'un individuel dont une séance a déjà été replanifiée. */
    | { kind: "overwriteReschedule"; rescheduledDate: string }

type Copy = { title: string; message?: string; confirm: string; cancel?: string; danger?: boolean }

function copyOf(
    dialog: TutorManageSessionDialogKind,
    impact: UITutorAbsenceImpactStudent[] | null,
    firstStudentName: string
): Copy {
    switch (dialog.kind) {
        case "hypercare":
            return {
                title: "Ta présence compte",
                message: dialog.message,
                confirm: "Contacter sur WhatsApp",
                cancel: "J'ai compris",
            }
        case "hypercareRefused":
            return {
                title: "Ta présence compte",
                message: dialog.message,
                confirm: "Contacter sur WhatsApp",
                cancel: "Fermer",
            }
        case "impactChanged":
            return {
                title: "L'impact de ton absence a changé",
                message: tutorChangeNotice(impact ?? []),
                confirm: "J'ai compris",
            }
        case "support":
            return {
                title:
                    dialog.reason === "other"
                        ? "Pour toute question, tu peux contacter le support"
                        : "Pour les changements qui ne sont pas ponctuels, merci de contacter le support",
                confirm: "Contacter le support",
                cancel: "Retour",
            }
        case "confirmDelay":
            return {
                title: "Signaler ce retard ?",
                message:
                    "Les élèves recevront un message à l'heure prévue de début du live, pour les prévenir de ton retard, et pour qu'ils commencent à travailler.",
                confirm: "Confirmer",
                cancel: "Annuler",
            }
        case "confirmStudentValidation":
            return {
                title: `Est-ce que tu confirmes que ${firstStudentName} a déjà validé ce changement ?`,
                confirm: "Oui",
                cancel: "Non",
            }
        case "overwriteReschedule":
            return {
                title: `Un live avait déjà été replanifié le ${formatDayTime(dialog.rescheduledDate, false, "sentence")}`,
                message: "Veux-tu écraser ce changement ?",
                confirm: "Confirmer",
                cancel: "Annuler",
                danger: true,
            }
    }
}

export type TutorManageSessionDialogProps = {
    /** La boîte ouverte. `null` : fermée. */
    dialog: TutorManageSessionDialogKind | null
    /** L'impact de l'absence — la boîte « a changé » nomme les élèves qui changent de masteur. */
    impact: UITutorAbsenceImpactStudent[] | null
    /** « Noah » — l'élève dont on confirme l'accord. */
    firstStudentName: string
    onConfirm: () => void
    onClose: () => void
    testID?: string
}

export function TutorManageSessionDialog(props: TutorManageSessionDialogProps) {
    const { dialog, impact, firstStudentName, onConfirm, onClose, testID } = props
    const copy = dialog != null ? copyOf(dialog, impact, firstStudentName) : null

    return (
        <ConfirmDialog
            visible={copy != null}
            title={copy?.title ?? ""}
            message={copy?.message}
            confirmLabel={copy?.confirm}
            cancelLabel={copy?.cancel}
            hideCancel={copy?.cancel == null}
            danger={copy?.danger ?? false}
            onConfirm={onConfirm}
            onCancel={onClose}
            testID={testID}
        />
    )
}
